import React, { useState, useEffect, FormEvent } from "react";
import { collection, addDoc, query, where, getDocs, getCountFromServer, Timestamp, setDoc, doc } from "firebase/firestore";
import { signInWithPopup } from "firebase/auth";
import { db, auth, googleProvider } from "../firebaseInit";
import * as FaIcons from "react-icons/fa6";

interface WaitlistCardProps {
  referrerCode: string | null;
  onSuccess?: () => void;
}

const WaitlistCard: React.FC<WaitlistCardProps> = ({ referrerCode, onSuccess }) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [waitlistStats, setWaitlistStats] = useState<null | { position: number; total: number; referrals: number }>(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [alreadySignedUp, setAlreadySignedUp] = useState(false);

  function randomDigits() {
    return Math.floor(1000 + Math.random() * 9000).toString();
  }

  async function generateUniqueReferralCode(firstName: string) {
    let code = "";
    let exists = true;
    while (exists) {
      code = (firstName.replace(/[^a-zA-Z0-9]/g, "").toLowerCase() + randomDigits());
      const q = query(collection(db, "waitlist"), where("referralCode", "==", code));
      const snapshot = await getDocs(q);
      exists = !snapshot.empty;
    }
    return code;
  }

  async function findWaitlistEntryByEmail(email: string): Promise<any | null> {
    const q = query(collection(db, "waitlist"), where("email", "==", email));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docData = snap.docs[0].data();
      return { ...docData, id: snap.docs[0].id };
    }
    return null;
  }

  // Fetch stats using server-side count aggregations — no document data from other users is read
  async function fetchStats(code: string, userCreatedAt: Timestamp) {
    const [totalSnap, positionSnap, referralsSnap] = await Promise.all([
      getCountFromServer(collection(db, "waitlist")),
      getCountFromServer(query(collection(db, "waitlist"), where("createdAt", "<", userCreatedAt))),
      getCountFromServer(query(collection(db, "waitlist"), where("referrerCode", "==", code))),
    ]);
    return {
      total: totalSnap.data().count,
      position: positionSnap.data().count + 1,
      referrals: referralsSnap.data().count,
    };
  }

  const handleSignup = async (data: { email: string; firstName: string }) => {
    if (!db) return;
    const existing = await findWaitlistEntryByEmail(data.email);
    if (existing && existing.referralCode) {
      setReferralCode(existing.referralCode);
      setAlreadySignedUp(true);
      setSubmitted(true);
      if (onSuccess) onSuccess();
      const stats = await fetchStats(existing.referralCode, existing.createdAt);
      setWaitlistStats(stats);
      return;
    }
    const code = await generateUniqueReferralCode(data.firstName);
    const createdAt = Timestamp.now();
    await addDoc(collection(db, "waitlist"), {
      email: data.email,
      firstName: data.firstName,
      referralCode: code,
      referrerCode: referrerCode || null,
      createdAt,
    });
    setReferralCode(code);
    setAlreadySignedUp(false);
    setSubmitted(true);
    if (onSuccess) onSuccess();
    const stats = await fetchStats(code, createdAt);
    setWaitlistStats(stats);
  };

  const handleGoogleSignIn = async () => {
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      if (!user.email) throw new Error('No email returned from Google sign-in.');
      const existing = await findWaitlistEntryByEmail(user.email);
      if (existing && existing.referralCode) {
        setReferralCode(existing.referralCode);
        setAlreadySignedUp(true);
        setSubmitted(true);
        if (onSuccess) onSuccess();
        const stats = await fetchStats(existing.referralCode, existing.createdAt);
        setWaitlistStats(stats);
        return;
      }
      const name = user.displayName?.split(' ')[0] || 'user';
      const code = await generateUniqueReferralCode(name);
      const createdAt = Timestamp.now();
      await setDoc(doc(db, 'waitlist', user.uid), {
        email: user.email,
        firstName: name,
        referralCode: code,
        referrerCode: referrerCode || null,
        createdAt,
      }, { merge: true });
      setReferralCode(code);
      setAlreadySignedUp(false);
      setSubmitted(true);
      if (onSuccess) onSuccess();
      const stats = await fetchStats(code, createdAt);
      setWaitlistStats(stats);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      alert('Failed to sign in with Google. Please try again.');
    }
  };

  const handleEmailFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await handleSignup({ email, firstName });
    setSubmitted(true);
    if (onSuccess) onSuccess();
  };

  useEffect(() => {
    function handleResize() {}
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const shareText = `I just joined the AmmoPantryAI waitlist! Track ammo, firearms, and gear with AI — completely private. Join me: https://ammopantryai.app/?ref=${referralCode} #AmmoPantryAI #Preparedness`;

  return (
    <div className="mx-auto max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700 flex flex-col items-center">
      <div className="text-2xl font-bold mb-2 text-center">Join the Waitlist</div>
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">Be the first to know when AmmoPantryAI launches on Google Play Store.</div>
      {!showEmailForm && !submitted && (
        <>
          <button
            className="w-full flex items-center justify-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 mb-3 font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            onClick={handleGoogleSignIn}
          >
            <span className="text-xl">{String.fromCodePoint(0x1F5A5)}</span>
            Continue with Google
          </button>
          <div className="w-full flex items-center my-2">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="mx-2 text-xs text-gray-400">OR</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>
          <button
            className="w-full flex items-center justify-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            onClick={() => setShowEmailForm(true)}
          >
            <span className="text-xl">✉️</span>
            Continue with email
          </button>
        </>
      )}
      {showEmailForm && !submitted && (
        <form className="w-full mt-2" onSubmit={handleEmailFormSubmit}>
          <input
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 mb-3 bg-white dark:bg-gray-900 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
            placeholder="First Name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            required
          />
          <input
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 mb-3 bg-white dark:bg-gray-900 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
            placeholder="Email Address"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <button
            type="submit"
            className="w-full bg-[#3b82f6] text-white font-semibold rounded-lg px-4 py-3 mt-2 hover:bg-[#2563eb] transition"
          >
            Join Waitlist
          </button>
          <button
            type="button"
            className="w-full mt-2 text-xs text-gray-400 hover:underline"
            onClick={() => setShowEmailForm(false)}
          >
            ← Back to sign up options
          </button>
        </form>
      )}
      {submitted && referralCode && (
        <div className="w-full text-center font-semibold mt-4 flex flex-col items-center gap-4">
          {alreadySignedUp ? (
            <div className="flex flex-col items-center gap-1">
              <div className="text-[#b89b56]">You've already joined the waitlist!</div>
              <div className="text-[#b89b56]">Welcome back</div>
            </div>
          ) : (
            <div className="text-green-600 dark:text-green-400">Thank you for joining the waitlist!</div>
          )}
          {waitlistStats && (
            <div className="flex flex-col items-center gap-1 text-base text-gray-900 dark:text-white font-normal">
              <div>Your position: <b>{waitlistStats.position}</b> out of <b>{waitlistStats.total}</b></div>
              <div>Referrals: <b>{waitlistStats.referrals}</b> (Invite more to move up!)</div>
            </div>
          )}
          <div className="text-base text-gray-900 dark:text-white font-normal">Share your unique link to move up the waitlist:</div>
          <div className="flex items-center gap-2 justify-center">
            <input
              className="w-[260px] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              value={`https://ammopantryai.app/?ref=${referralCode}`}
              readOnly
            />
            <button
              className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-3 py-2 rounded-lg text-sm font-semibold"
              onClick={() => navigator.clipboard.writeText(`https://ammopantryai.app/?ref=${referralCode}`)}
            >
              Copy Link
            </button>
          </div>
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black hover:bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1"
            >
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M22.162 5.656c.015.211.015.423.015.636 0 6.49-4.943 13.986-14.009 13.986A13.94 13.94 0 0 1 0 21.543c.627.073 1.254.104 1.896.104a9.86 9.86 0 0 0 6.102-2.104 4.93 4.93 0 0 1-4.6-3.417c.308.047.616.073.938.073.453 0 .89-.06 1.304-.173a4.92 4.92 0 0 1-3.95-4.827v-.062a4.936 4.936 0 0 0 2.229.616 4.92 4.92 0 0 1-2.188-4.099c0-.9.242-1.748.666-2.475a13.978 13.978 0 0 0 10.141 5.146c-.084-.386-.127-.762-.127-1.124 0-2.722 2.212-4.932 4.942-4.932 1.422 0 2.707.6 3.61 1.56a9.864 9.864 0 0 0 3.127-1.195 4.932 4.932 0 0 1-2.165 2.724 9.864 9.864 0 0 0 2.828-.775 10.02 10.02 0 0 1-2.48 2.56z"/></svg>
              X
            </a>
            <button
              className="bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 hover:from-pink-600 hover:to-yellow-600 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1"
              onClick={() => {
                navigator.clipboard.writeText(shareText);
                alert('Message copied! Open Instagram and paste it in your story or post.');
              }}
            >
              <FaIcons.FaInstagram className="text-lg" />
              Instagram
            </button>
            <button
              className="bg-black hover:bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1"
              onClick={() => {
                navigator.clipboard.writeText(shareText);
                alert('Message copied! Open TikTok and paste it in your video or bio.');
              }}
            >
              <FaIcons.FaTiktok className="text-lg" />
              TikTok
            </button>
            <button
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1"
              onClick={() => {
                const link = document.createElement('a');
                link.href = '/ammopantryai.png';
                link.download = 'ammopantryai.png';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M12 16.5l4-4h-3V3h-2v9.5H8l4 4zm8 2.5H4v2h16v-2z"/></svg>
              Download Story Image
            </button>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">Invite friends and family — each signup with your link moves you up the list!</div>
        </div>
      )}
      <div className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">
        <div>By continuing, you agree with the</div>
        <div>
          <a href="/terms-of-use" className="underline mx-1" target="_blank" rel="noopener noreferrer">Terms</a>
          and
          <a href="/privacy-policy" className="underline mx-1" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
};

export default WaitlistCard;
